import { Chart } from "primereact/chart";
import moment from "moment";
import React, { useMemo } from "react";

const DELIVERY_LABELS = {
    DeliveryWithPickup: "Livraison avec reprise",
    DeliveryWithPickupAndSetup: "Livraison, reprise et installation",
    DeliveryOnly: "Livraison uniquement",
    PickupOnly: "Reprise uniquement",
};

const DELIVERY_SHORT_LABELS = {
    DeliveryWithPickup: "Livraison + reprise",
    DeliveryWithPickupAndSetup: "Avec installation",
    DeliveryOnly: "Livraison seule",
    PickupOnly: "Reprise seule",
};

const BAR_COLOR = "#00899e";

const formatCurrency = (value, digits = 0) =>
    (value ?? 0).toLocaleString("fr-FR", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });

const formatNumber = (value, digits = 0) =>
    (value ?? 0).toLocaleString("fr-FR", { maximumFractionDigits: digits });

const formatPercent = (value) => `${(value ?? 0).toLocaleString("fr-FR", { maximumFractionDigits: 1 })}%`;

const ratio = (value, total) => (total > 0 ? value / total : 0);

const plural = (count, singular, pluralForm = `${singular}s`) => `${formatNumber(count)} ${count > 1 ? pluralForm : singular}`;

const formatHours = (hours) => {
    const totalMinutes = Math.round((hours ?? 0) * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    if (h === 0) {
        return `${m} min`;
    }
    return m === 0 ? `${h} h` : `${h} h ${String(m).padStart(2, "0")}`;
};

const deliveryLabel = (delivery) => DELIVERY_LABELS[delivery] ?? delivery;

function Kpi({ label, value, hint, tone }) {
    return (
        <div className={`stats-kpi-card stats-delivery-kpi ${tone ? `stats-kpi-${tone}` : ""}`}>
            <span className="stats-kpi-label">{label}</span>
            <strong className="stats-kpi-value">{value}</strong>
            {hint && <span className="stats-kpi-hint">{hint}</span>}
        </div>
    );
}

function RouteMetric({ label, value, hint }) {
    return (
        <div className="stats-delivery-route-metric">
            <span>{label}</span>
            <strong>{value}</strong>
            {hint && <small>{hint}</small>}
        </div>
    );
}

function DeliveryStats({ stats }) {
    const period = stats?.period;
    const previous = stats?.previousPeriod;
    const allTime = stats?.allTime;
    const route = stats?.route;
    const upcoming = stats?.upcoming;
    const types = stats?.types ?? [];
    const months = stats?.monthlyStats ?? [];
    const rentals = stats?.rentals ?? [];

    const chart = useMemo(() => {
        const labels = months.map((month) =>
            new Date(month.month).toLocaleDateString("fr", { year: "2-digit", month: "2-digit" })
        );
        return {
            data: {
                labels,
                datasets: [
                    {
                        label: "CA livraison",
                        data: months.map((month) => month.deliveryRevenue),
                        backgroundColor: BAR_COLOR,
                        hoverBackgroundColor: BAR_COLOR,
                        borderRadius: { topLeft: 4, topRight: 4 },
                        borderSkipped: "bottom",
                        maxBarThickness: 36,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: "index", intersect: false },
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        callbacks: {
                            title: (items) => {
                                const month = months[items[0]?.dataIndex];
                                return month ? new Date(month.month).toLocaleDateString("fr-FR", { month: "long", year: "numeric" }) : "";
                            },
                            label: (context) => `CA livraison : ${formatCurrency(context.parsed.y)}`,
                            afterLabel: (context) => {
                                const month = months[context.dataIndex];
                                if (!month || month.deliveredRentalsCount === 0) {
                                    return "Aucune livraison";
                                }
                                return `${plural(month.deliveredRentalsCount, "location livrée", "locations livrées")} · ${plural(month.tripsCount, "passage")}`;
                            },
                        },
                    },
                },
                scales: {
                    x: { grid: { display: false } },
                    y: {
                        beginAtZero: true,
                        grid: { color: "rgba(15, 23, 42, 0.06)" },
                        border: { display: false },
                        ticks: { callback: (value) => formatCurrency(Number(value)), maxTicksLimit: 6 },
                    },
                },
            },
        };
    }, [months]);

    if (!period) {
        return (
            <section className="stats-section card">
                <div className="stats-section-header">
                    <div>
                        <h2>Livraisons</h2>
                    </div>
                </div>
                <p className="stats-empty-state">Statistiques de livraison indisponibles.</p>
            </section>
        );
    }

    const deliveryShare = ratio(period.deliveryRevenue, period.rentalsRevenue) * 100;
    const deliveryRate = ratio(period.deliveredRentalsCount, period.rentalsCount) * 100;
    const revenueDelta = period.deliveryRevenue - (previous?.deliveryRevenue ?? 0);
    const averagePerTrip = ratio(period.deliveryRevenue, period.chargedTripsCount);
    const averageBasketDelivered = ratio(period.deliveredRentalsRevenue, period.deliveredRentalsCount);
    const averageBasketOthers = ratio(
        period.rentalsRevenue - period.deliveredRentalsRevenue,
        period.rentalsCount - period.deliveredRentalsCount
    );
    const totalTypesRevenue = types.reduce((acc, type) => acc + type.deliveryRevenue, 0);
    const totalTypesRentals = types.reduce((acc, type) => acc + type.rentalsCount, 0);
    const hasMonthlyData = months.some((month) => month.deliveredRentalsCount > 0);

    const rentalsFeesTotal = rentals.reduce((acc, rental) => acc + rental.deliveryRevenue, 0);
    const rentalsRevenueTotal = rentals.reduce((acc, rental) => acc + rental.rentalRevenue, 0);
    const allTimeSince = stats?.firstDeliveryDate ? ` depuis le ${moment(stats.firstDeliveryDate).format("DD/MM/YYYY")}` : "";

    const routeRevenuePerKm = ratio(route?.deliveryRevenue, route?.kilometers);
    const routeRevenuePerHour = ratio(route?.deliveryRevenue, route?.hours);
    const routeNet = (route?.deliveryRevenue ?? 0) - (route?.fuelCost ?? 0);

    return (
        <section className="stats-section card stats-delivery" data-cy="deliveryStats">
            <div className="stats-section-header">
                <div>
                    <h2>Livraisons</h2>
                    <p>Frais de livraison, de reprise et d'installation des locations terminées sur la période.</p>
                </div>
                <div className="stats-section-side">
                    <span className="stats-side-label">CA livraison</span>
                    <strong className="stats-side-value">{formatCurrency(period.deliveryRevenue)}</strong>
                    <span className="stats-side-subvalue">{formatPercent(deliveryShare)} du CA des locations</span>
                    <span className={`stats-side-footnote ${revenueDelta >= 0 ? "stats-delta-positive" : "stats-delta-negative"}`}>
                        {revenueDelta >= 0 ? "+" : ""}{formatCurrency(revenueDelta)} vs période précédente
                    </span>
                </div>
            </div>

            <div className="stats-delivery-body">
                <div className="stats-kpi-grid stats-delivery-kpis">
                    <Kpi
                        label="Locations livrées"
                        value={formatNumber(period.deliveredRentalsCount)}
                        hint={`${formatPercent(deliveryRate)} des locations · ${plural(period.tripsCount, "passage")}`}
                    />
                    <Kpi
                        label="Frais moyens par passage"
                        value={period.chargedTripsCount > 0 ? formatCurrency(averagePerTrip) : "—"}
                        hint={period.chargedTripsCount > 0 ? `sur ${plural(period.chargedTripsCount, "passage facturé", "passages facturés")}` : "Aucun passage facturé"}
                    />
                    <Kpi
                        label="Panier moyen livré"
                        value={period.deliveredRentalsCount > 0 ? formatCurrency(averageBasketDelivered) : "—"}
                        hint={period.rentalsCount > period.deliveredRentalsCount ? `contre ${formatCurrency(averageBasketOthers)} sans livraison` : null}
                    />
                    <Kpi
                        label="À venir"
                        value={formatCurrency(upcoming?.deliveryRevenue)}
                        hint={upcoming?.tripsCount > 0
                            ? `${plural(upcoming.tripsCount, "passage planifié", "passages planifiés")} · prochain le ${moment(upcoming.nextTripDate).format("DD/MM")}`
                            : "Aucun passage planifié"}
                    />
                </div>

                {allTime && (
                    <div className="stats-delivery-alltime">
                        <span className="stats-delivery-alltime-title">Depuis le début</span>
                        <div className="stats-kpi-grid stats-delivery-kpis">
                            <Kpi
                                label="Frais de livraison"
                                value={formatCurrency(allTime.deliveryRevenue)}
                                hint={`${plural(allTime.chargedRentalsCount, "location facturée", "locations facturées")} · ${plural(allTime.chargedTripsCount, "passage")}`}
                            />
                            <Kpi
                                label="Locations livrées"
                                value={formatCurrency(allTime.deliveredRentalsRevenue)}
                                hint={`${plural(allTime.deliveredRentalsCount, "location", "locations")}${allTimeSince} · ${formatPercent(ratio(allTime.deliveredRentalsRevenue, allTime.rentalsRevenue) * 100)} du CA`}
                            />
                        </div>
                    </div>
                )}

                {period.freeRentalsCount > 0 && (
                    <p className="stats-delivery-note" role="note">
                        <i className="pi pi-info-circle" aria-hidden="true" />
                        <span>
                            {period.freeRentalsCount > 1
                                ? `${formatNumber(period.freeRentalsCount)} locations livrées n'ont pas de frais de livraison saisis`
                                : "1 location livrée n'a pas de frais de livraison saisis"}{" "}
                            (livraison offerte ou comprise dans un prix personnalisé sans détail) : elles comptent dans les passages, pas dans le CA livraison.
                        </span>
                    </p>
                )}

                <div className="stats-delivery-grid">
                    <div className="stats-delivery-panel">
                        <h3>CA livraison sur 12 mois</h3>
                        {hasMonthlyData ? (
                            <div className="stats-chart stats-delivery-chart" role="img" aria-label="CA livraison par mois sur 12 mois glissants">
                                <Chart type="bar" data={chart.data} options={chart.options} />
                            </div>
                        ) : (
                            <p className="stats-empty-state">Aucune livraison sur les 12 derniers mois.</p>
                        )}
                    </div>

                    <div className="stats-delivery-panel">
                        <h3>Par prestation</h3>
                        {types.length === 0 ? (
                            <p className="stats-empty-state">Aucune livraison sur cette période.</p>
                        ) : (
                            <ul className="stats-ranking stats-delivery-ranking">
                                {types.map((type) => {
                                    const share = totalTypesRevenue > 0
                                        ? ratio(type.deliveryRevenue, totalTypesRevenue) * 100
                                        : ratio(type.rentalsCount, totalTypesRentals) * 100;
                                    return (
                                        <li key={type.delivery} className="stats-ranking-item">
                                            <div className="stats-ranking-main">
                                                <strong>{deliveryLabel(type.delivery)}</strong>
                                                <span>
                                                    {formatCurrency(type.deliveryRevenue)} · {plural(type.rentalsCount, "location")} · {plural(type.tripsCount, "passage")}
                                                </span>
                                            </div>
                                            <div className="stats-ranking-bar">
                                                <div className="stats-ranking-bar-fill stats-delivery-bar-fill" style={{ width: `${Math.max(0, Math.min(100, share))}%` }} />
                                            </div>
                                            <span className="stats-ranking-share">{formatPercent(share)}</span>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                        {totalTypesRevenue === 0 && types.length > 0 && (
                            <small className="stats-delivery-footnote">Répartition en nombre de locations (aucun frais facturé).</small>
                        )}
                    </div>
                </div>

                <div className="stats-delivery-panel">
                    <h3>Route et carburant</h3>
                    {route?.rentalsCount > 0 ? (
                        <>
                            <div className="stats-delivery-route">
                                <RouteMetric label="Kilomètres parcourus" value={`${formatNumber(route.kilometers)} km`} hint={`${plural(route.tripsCount, "passage")} aller-retour`} />
                                <RouteMetric label="Temps de route" value={formatHours(route.hours)} />
                                <RouteMetric label="Recette par km" value={formatCurrency(routeRevenuePerKm, 2)} />
                                <RouteMetric label="Recette par heure de route" value={formatCurrency(routeRevenuePerHour)} />
                                <RouteMetric
                                    label="Carburant estimé"
                                    value={formatCurrency(route.fuelCost)}
                                    hint={`${formatNumber(route.fuelLiters, 1)} L`}
                                />
                                <RouteMetric label="Reste après carburant" value={formatCurrency(routeNet)} hint="péages non déduits" />
                            </div>
                            <small className="stats-delivery-footnote">
                                Calculé sur {route.rentalsCount} des {plural(period.deliveredRentalsCount, "location livrée", "locations livrées")} dont le trajet a été estimé
                                avec « Estimer les frais ». Carburant : {formatNumber(route.consumptionLitersPer100Km, 1)} L/100 km
                                à {formatCurrency(route.fuelPricePerLiter, 2)}/L (Paramètres › Livraison).
                            </small>
                        </>
                    ) : (
                        <p className="stats-empty-state">
                            Le kilométrage, le temps de route et le coût du carburant apparaîtront pour les livraisons chiffrées
                            avec « Estimer les frais » dans le formulaire de location.
                        </p>
                    )}
                </div>

                <div className="stats-delivery-panel">
                    <h3>Détail des livraisons</h3>
                    {rentals.length === 0 ? (
                        <p className="stats-empty-state">Aucune location livrée sur cette période.</p>
                    ) : (
                        <div className="stats-tools-table stats-delivery-table">
                            <div className="stats-tools-table-header stats-delivery-row">
                                <span>Fin</span>
                                <span>Client</span>
                                <span>Prestation</span>
                                <span>Adresse</span>
                                <span>Trajet</span>
                                <span>Frais</span>
                                <span>Location</span>
                            </div>
                            {rentals.map((rental) => (
                                <a key={rental.publicId} href={`/${rental.publicId}`} className="stats-tools-row stats-delivery-row">
                                    <span className="stats-delivery-cell-date">{moment(rental.endDate).format("DD/MM/YYYY")}</span>
                                    <span className="stats-delivery-cell-client" title={`${rental.clientName ?? ""}${rental.toolName ? ` · ${rental.toolName}` : ""}`}>{rental.clientName}</span>
                                    <span className="stats-delivery-cell-type" title={deliveryLabel(rental.delivery)}>
                                        {DELIVERY_SHORT_LABELS[rental.delivery] ?? deliveryLabel(rental.delivery)}
                                    </span>
                                    <span className="stats-delivery-cell-address" title={rental.deliveryAddress ?? ""}>{rental.deliveryAddress || "—"}</span>
                                    <span className="stats-delivery-cell-route">
                                        {rental.oneWayKilometers != null
                                            ? `${formatNumber(rental.oneWayKilometers, 1)} km${rental.oneWayMinutes ? ` · ${rental.oneWayMinutes} min` : ""}`
                                            : "—"}
                                    </span>
                                    <strong className={`stats-delivery-cell-fees ${rental.deliveryRevenue > 0 ? "" : "stats-delivery-free"}`}>
                                        {rental.deliveryRevenue > 0 ? formatCurrency(rental.deliveryRevenue) : "Sans frais"}
                                    </strong>
                                    <span className="stats-delivery-cell-total">{formatCurrency(rental.rentalRevenue)}</span>
                                </a>
                            ))}
                            <div className="stats-tools-row stats-delivery-row stats-delivery-total" data-cy="deliveryTotals">
                                <span className="stats-delivery-total-label">
                                    Total · {plural(rentals.length, "location livrée", "locations livrées")}
                                </span>
                                <strong className="stats-delivery-total-fees" data-label="Frais de livraison">{formatCurrency(rentalsFeesTotal)}</strong>
                                <strong className="stats-delivery-total-rentals" data-label="Locations">{formatCurrency(rentalsRevenueTotal)}</strong>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

export default DeliveryStats;
